import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CurrentProfile, ProfileOption } from '../types/profile';

interface ProfileSelectionModalProps {
  open: boolean;
  enforceSelection?: boolean;
  availableProfiles: ProfileOption[];
  currentProfile: CurrentProfile | null;
  isLoading: boolean;
  onRefresh: () => Promise<void>;
  onConfirm: (profile: CurrentProfile) => void;
  onClose: () => void;
}

const ProfileSelectionModal: React.FC<ProfileSelectionModalProps> = ({
  open,
  enforceSelection = false,
  availableProfiles,
  currentProfile,
  isLoading,
  onRefresh,
  onConfirm,
  onClose,
}) => {
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedProfileId(null);
      return;
    }

    if (currentProfile) {
      setSelectedProfileId(`${currentProfile.tipo}-${currentProfile.referenciaId}`);
    }
  }, [currentProfile, open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !enforceSelection) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.classList.add('modal-open');

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.classList.remove('modal-open');
    };
  }, [open, enforceSelection, onClose]);

  const handleBackdropClick = useCallback(() => {
    if (!enforceSelection) {
      onClose();
    }
  }, [enforceSelection, onClose]);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  }, [onRefresh]);

  const handleSelectCard = useCallback((profileKey: string) => {
    setSelectedProfileId(profileKey);
  }, []);

  const selectedProfile = useMemo(() => {
    if (!selectedProfileId) return null;
    const firstDashIndex = selectedProfileId.indexOf('-');
    if (firstDashIndex === -1) return null;

    const tipo = selectedProfileId.substring(0, firstDashIndex) as CurrentProfile['tipo'];
    const referenciaId = selectedProfileId.substring(firstDashIndex + 1);

    const profile = availableProfiles.find(
      (p) => p.tipo === tipo && p.referenciaId === referenciaId,
    );

    if (!profile) return null;

    return {
      tipo: profile.tipo,
      referenciaId: profile.referenciaId,
      nome: profile.nome,
      imagemUrl: profile.imagemUrl,
    } as CurrentProfile;
  }, [availableProfiles, selectedProfileId]);

  const handleConfirm = useCallback(() => {
    if (!selectedProfile) return;
    onConfirm(selectedProfile);
  }, [onConfirm, selectedProfile]);

  if (!open) {
    return null;
  }

  const hasLeagueOptions = availableProfiles.some((p) => p.tipo === 'LIGA');
  const modalTitle = hasLeagueOptions
    ? 'Selecione o perfil para continuar'
    : 'Seu perfil';

  return (
    <>
      <div
        className="modal-backdrop fade show"
        style={{ position: 'fixed', inset: 0, zIndex: 1040 }}
        onClick={handleBackdropClick}
      />
      <div
        className="modal fade show d-block"
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        style={{ position: 'fixed', inset: 0, zIndex: 1050, overflowY: 'auto' }}
        onClick={handleBackdropClick}
      >
        <div
          className="modal-dialog modal-lg modal-dialog-centered"
          role="document"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="modal-content border-0 shadow-lg">
            <div className="modal-header border-0 pb-0">
              <div>
                <h5 className="modal-title fw-700 mb-1">{modalTitle}</h5>
                <p className="text-grey-500 mb-0 font-xsss">
                  {hasLeagueOptions
                    ? 'Escolha qual perfil deseja usar nesta sessão.'
                    : 'Confirme para continuar com o perfil disponível.'}
                </p>
              </div>
              {!enforceSelection && (
                <button type="button" className="btn-close" aria-label="Fechar" onClick={onClose} />
              )}
            </div>
            <div className="modal-body">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <span className="text-grey-600 font-xsss">
                  {hasLeagueOptions
                    ? 'Selecione a liga ou o perfil de atleta desejado.'
                    : 'Você possui apenas um perfil disponível no momento.'}
                </span>
                <button
                  type="button"
                  onClick={handleRefresh}
                  disabled={isRefreshing || isLoading}
                  className="btn btn-link text-decoration-none font-xsss fw-600 px-0"
                >
                  {isRefreshing ? 'Atualizando...' : 'Recarregar perfis'}
                </button>
              </div>

              {(isLoading && availableProfiles.length === 0) && (
                <div className="alert alert-info border-info text-grey-600 font-xsss">
                  Carregando perfis...
                </div>
              )}

              {!isLoading && availableProfiles.length === 0 && (
                <div className="alert alert-warning border-warning text-grey-600 font-xsss">
                  Nenhum perfil disponível no momento.
                </div>
              )}

              {availableProfiles.length === 1 && (
                <div className="alert alert-info border-info text-grey-600 font-xsss">
                  Apenas um perfil está disponível. Confirme para continuar.
                </div>
              )}

              <div className="row">
                {availableProfiles.map((profile) => {
                  const profileKey = `${profile.tipo}-${profile.referenciaId}`;
                  const isSelected = selectedProfileId === profileKey;

                  return (
                    <div key={profileKey} className="col-md-6 mb-3">
                      <div
                        className={`card border-2 ${
                          isSelected ? 'border-primary' : 'border-light'
                        } cursor-pointer`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleSelectCard(profileKey)}
                      >
                        <div className="card-body p-4">
                          <div className="d-flex align-items-center">
                            <div className="me-3">
                              <i
                                className={`bi ${
                                  profile.tipo === 'ATLETA' ? 'bi-person-circle' : 'bi-trophy'
                                } font-xxl text-primary`}
                              ></i>
                            </div>
                            <div className="flex-grow-1">
                              <h5 className="fw-600 mb-1">{profile.nome}</h5>
                              <p className="text-grey-500 mb-0 font-xsss">
                                {profile.tipo === 'ATLETA' ? 'Perfil de Atleta' : 'Liga'}
                              </p>
                            </div>
                            {isSelected && (
                              <div>
                                <i className="bi bi-check-circle-fill text-primary font-lg"></i>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="modal-footer border-0 pt-0 d-flex justify-content-end">
              <button
                type="button"
                onClick={handleConfirm}
                disabled={!selectedProfile || isLoading}
                className="btn btn-primary fw-600 px-4"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProfileSelectionModal;








